import React from 'react';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { Link } from 'react-router-dom';
import { Lock, ShieldCheck, KeyRound, Server, Eye, AlertTriangle, FileCheck2, Users } from 'lucide-react';

const sections = [
  {
    icon: Lock,
    title: 'Encryption',
    body: 'TLS 1.2+ for all traffic in transit. AES-256 at rest for databases, file storage and backups. Encryption keys managed by our cloud provider with regular rotation.',
  },
  {
    icon: KeyRound,
    title: 'Access control',
    body: 'Least-privilege role-based access. Single sign-on and mandatory MFA for all NoQ5 staff. Production access limited to a named on-call rotation and logged.',
  },
  {
    icon: Server,
    title: 'Data residency',
    body: 'Customer data is hosted in the European Union (Frankfurt). Backups remain inside the EU. Subprocessors processing data outside the EEA are bound by EU SCCs.',
  },
  {
    icon: Eye,
    title: 'Logging and monitoring',
    body: 'Application, database and authentication events are centrally logged with retention of at least 13 months. Anomaly detection on auth and admin actions.',
  },
  {
    icon: AlertTriangle,
    title: 'Vulnerability management',
    body: 'Continuous dependency scanning, weekly automated security tests and yearly third-party penetration testing. Critical vulnerabilities patched within 7 days.',
  },
  {
    icon: ShieldCheck,
    title: 'Incident response',
    body: 'Documented playbook with on-call rotation. Customers are notified of confirmed personal-data breaches within 72 hours, in line with GDPR Article 33.',
  },
  {
    icon: Users,
    title: 'Personnel',
    body: 'Background checks where permitted. Annual security and privacy training. Contractual confidentiality obligations that survive termination.',
  },
  {
    icon: FileCheck2,
    title: 'AI provider posture',
    body: 'We use OpenAI and Anthropic via their enterprise APIs with zero-retention settings. Customer prompts and outputs are not used to train their public foundation models.',
  },
];

const Security = () => {
  return (
    <Layout>
      <SEOHead
        title="Security & Trust - NoQ5.ai"
        description="How NoQ5 secures customer data: encryption, access control, EU data residency, logging, incident response, AI provider posture and SOC 2 roadmap."
        canonicalUrl="https://noq5.ai/security"
      />
      <div className="container px-4 md:px-6 py-12 max-w-5xl mx-auto">
        <header className="mb-12 max-w-2xl">
          <p className="text-sm text-muted-foreground">Trust Center</p>
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mt-2">Security at NoQ5</h1>
          <p className="text-muted-foreground mt-4 leading-relaxed">
            NoQ5 sits inside the workflow that revenue teams trust most: their CRM, their inbox and their
            calls. We treat that responsibility with engineering discipline and clear documentation. This
            page is the source of truth for our security posture.
          </p>
        </header>

        <section className="grid md:grid-cols-2 gap-4 mb-12">
          {sections.map((s) => (
            <div key={s.title} className="bg-card border border-border rounded-sm p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <s.icon className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-foreground mb-2">{s.title}</h2>
                  <p className="text-muted-foreground leading-relaxed">{s.body}</p>
                </div>
              </div>
            </div>
          ))}
        </section>

        <section className="bg-card border border-border rounded-sm p-6 mb-6">
          <h2 className="text-xl font-semibold text-foreground mb-3">Compliance roadmap</h2>
          <ul className="space-y-2 text-muted-foreground">
            <li>· <strong className="text-foreground">GDPR:</strong> in production today. See our <Link to="/privacy-policy" className="text-primary hover:underline">Privacy Policy</Link> and <Link to="/dpa" className="text-primary hover:underline">DPA</Link>.</li>
            <li>· <strong className="text-foreground">SOC 2 Type I:</strong> targeted Q3 2026.</li>
            <li>· <strong className="text-foreground">SOC 2 Type II:</strong> targeted Q1 2027.</li>
            <li>· <strong className="text-foreground">ISO 27001:</strong> roadmap evaluation in 2027.</li>
          </ul>
        </section>

        <section className="bg-card border border-border rounded-sm p-6 mb-6">
          <h2 className="text-xl font-semibold text-foreground mb-3">Customer controls</h2>
          <ul className="space-y-2 text-muted-foreground">
            <li>· Approval-required mode: every CRM write is reviewed by a human before commit.</li>
            <li>· Per-user scopes: limit which CRM objects and fields NoQ5 can read or write.</li>
            <li>· Configurable retention for transcripts and prompt history.</li>
            <li>· Full export and one-click account deletion.</li>
            <li>· Audit log of every action NoQ5 performs in your CRM.</li>
          </ul>
        </section>

        <section className="bg-card border border-border rounded-sm p-6">
          <h2 className="text-xl font-semibold text-foreground mb-3">Reporting a vulnerability</h2>
          <p className="text-muted-foreground">
            Email <a className="text-primary hover:underline" href="mailto:batiste@noq5.ai">batiste@noq5.ai</a>{' '}
            with subject "Security disclosure". We commit to acknowledge within 2 business days. We do not
            currently run a public bounty programme but we credit responsible disclosure on this page.
          </p>
        </section>
      </div>
    </Layout>
  );
};

export default Security;
